<template>
  <v-container class="pt-5">
    <div class="d-flex justify-space-between align-center">
      <h1>Laaturaportti</h1>
      <v-btn class="mb-2" color="primary" @click="exportCsv">
        <v-icon class="mr-2">mdi-file-export</v-icon>
        Export
      </v-btn>
    </div>
    <div class="ag-theme-quartz" style="height: calc(100vh - 220px); width: 100%;">
      <ag-grid-vue
        style="width: 100%; min-height: 500px; height: 100%;"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
      />
    </div>
  </v-container>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { AG_GRID_LOCALE_FI } from '@/utils/data-tool/dataToolHelper';
import ax from '@/utils/axios/axios.js';

export default {
  name: 'Laaturaportti',
  components: {
    AgGridVue
  },
  modules: [RowGroupingModule],
  data() {
    return {
      columnDefs: [
        { headerName: 'ID', field: 'id', width: 90, filter: true, enableRowGroup: true, enableValue: true, hide: true },
        { headerName: 'Peli pelattu', hide: true, field: 'game_date', width: 120, filter: 'agDateColumnFilter', enableRowGroup: true, enableValue: true,
          filterParams: {
            comparator: (filterDate, cellValue) => {
              if (!cellValue) return 0;
              const cellDate = new Date(cellValue);
              if (isNaN(cellDate)) return 0;
              return cellDate.getTime() - filterDate.getTime();
            }
          },
          valueFormatter: params => {
            if (!params.value) return '';
            const d = new Date(params.value);
            if (isNaN(d)) return params.value;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}.${month}.${year}`;
          } },
        { headerName: 'Pelin nimi', field: 'game_title', width: 140, filter: true, enableRowGroup: true, enableValue: true },
        { headerName: 'Sarja', field: 'league_name', width: 100, filter: true, enableRowGroup: true, enableValue: true, valueFormatter: params => { return params.value?.toUpperCase() || '-' } },
        { headerName: 'Analysoija', field: 'analyzer_name', width: 120, filter: true, enableRowGroup: true, enableValue: true },
        { headerName: 'Kohde', field: 'quality_control_item_id', width: 120, filter: true, enableRowGroup: true, enableValue: true, hide: true },
        { headerName: 'Kohteen nimi', field: 'item_title', enablePivot: true, width: 160, filter: true, enableRowGroup: true, enableValue: true, allowedAggFuncs: ['count', 'percentage'] },
        { headerName: 'Virheet', field: 'errors_found', width: 90, filter: true, enableRowGroup: true, enableValue: true, allowedAggFuncs: ['sum', 'avg', 'min', 'max'] },
        { headerName: 'Huomiot', field: 'notes', flex: 1, filter: true, enableRowGroup: true, enableValue: true },
        { headerName: 'Tarkastettu pvm', field: 'date', width: 120, filter: 'agDateColumnFilter', enableRowGroup: true, enableValue: true, hide: false,
          filterParams: {
            comparator: (filterDate, cellValue) => {
              if (!cellValue) return 0;
              const cellDate = new Date(cellValue);
              if (isNaN(cellDate)) return 0;
              return cellDate.getTime() - filterDate.getTime();
            }
          },
          valueFormatter: params => {
            if (!params.value) return '';
            const d = new Date(params.value);
            if (isNaN(d)) return params.value;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}.${month}.${year}`;
          } },
        { headerName: 'Tarkastaja', field: 'quality_controller', width: 160, filter: true, enableRowGroup: true, enableValue: true, hide: true },
      ],
      rowData: [],
      gridOptions: {
        localeText: AG_GRID_LOCALE_FI,
        suppressCellFocus: true,
        groupDisplayType: 'multipleColumns',
        rowHeight: 30,
        pagination: true,
        rowGroupPanelShow: 'always',
        aggFuncs: {
          'percentage': (params) => {
            // In pivot tables, the current value should be the count of values
            const currentValue = params.values ? params.values.length : 0;

            // For percentage calculation in pivot tables, we need to get the total count
            // from the current quality controller group only
            if (params.rowNode && params.rowNode.aggData) {
              let total = 0;
              Object.entries(params.rowNode.aggData).forEach(([, value]) => {
                if (typeof value === 'object' && value !== null && value.value !== undefined) {
                  total += (value.value || 0);
                }
              });

              if (total === 0) return '0%';
              const percentage = Math.round((currentValue / total) * 100);
              return percentage + '%';
            }

            return '0%';
          }
        },
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Sarakkeet',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel'
            },
            {
              id: 'filters',
              labelDefault: 'Suodattimet',
              labelKey: 'filters',
              iconKey: 'filter',
              toolPanel: 'agFiltersToolPanel'
            }
          ],
          defaultToolPanel: 'columns'
        }
      },
      gridApi: null,
      columnApi: null
    }
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      // Set up default state: group by analyzer, toolbar open, not pivot mode, average of errors
      this.columnApi.setRowGroupColumns(['analyzer_name']);
      this.columnApi.setPivotMode(false);
      this.columnApi.setValueColumns([
        { colId: 'errors_found', aggFunc: 'avg' }
      ]);
    },
    exportCsv() {
      if (this.gridApi) {
        // Get filtered data
        // const filteredRows = [];
        // this.gridApi.forEachNodeAfterFilterAndSort(node => {
        //   if (node.data) filteredRows.push(node.data);
        // });
        // const uniqueAnalyzers = [...new Set(filteredRows.map(row => row.analyzer_name).filter(Boolean))];

        // let prependContent = '';
        // if (uniqueAnalyzers.length === 1) {
        //   prependContent = `Analysoija: ${uniqueAnalyzers[0]}\n`;
        // }

        this.gridApi.exportDataAsExcel({
          // prependContent,
        });
      }
    }
  },
  async mounted() {
    try {
      const res = await ax.get('/quality/result');
      this.rowData = res.data;
    } catch (e) {
      this.rowData = [];
    }
  }
}
</script>